import type { ServicePageData } from './types';

const raisedLettering: ServicePageData = {
  slug: 'raised-lettering',
  eyebrow: 'Signage',
  title: 'Give your fascia depth with',
  titleAccent: 'raised 3D lettering.',
  intro:
    'Flat vinyl works, but raised letters and logos catch the light and cast a shadow that reads from across the street. We cut, build and fit your lettering in-house, from a simple flat-cut name to a fully illuminated shop sign.',
  heroImage: '/home/signboards/acrylic-panel.jpg',

  useCases: [
    { title: 'Shop fascias', text: 'A name that reads properly in daylight and stands proud of the board behind it.' },
    { title: 'Reception walls', text: 'A polished, professional welcome for visitors and clients.' },
    { title: 'Office and unit signage', text: 'Company name and logo fitted directly to render, brick or a feature wall.' },
    { title: 'Meeting rooms', text: 'Individual letters or a logo, mounted flush or on stand-offs.' },
    { title: 'Rebrands and refits', text: 'Swap out old lettering for a cleaner, more modern finish.' },
  ],

  optionsHeading: 'Choose your lettering',
  optionsIntro: 'Every set of letters is cut and finished to your exact logo file, not a stock font.',
  options: [
    {
      id: 'flat-cut-acrylic',
      title: 'Flat-cut acrylic',
      lead: 'Crisp, precise letters with a clean edge, ideal for a modern look.',
      body: 'Cut to your exact logo outline from solid acrylic sheet, finished with polished or flame-polished edges. Works flush-mounted or held off the wall on locators for a shadow gap.',
      image: '/home/signboards/acrylic-panel.jpg',
      specs: [
        { label: 'Colours', value: 'Clear or opaque colours, plus mirror finishes for interiors.' },
        { label: 'Finish', value: 'Polished or flame-polished edges.' },
        { label: 'Fixings', value: 'Flush-fixed or stand-off locators for a shadow gap.' },
      ],
    },
    {
      id: 'flat-cut-foam-pvc',
      title: 'Flat-cut foam PVC',
      lead: 'A lightweight, cost-effective alternative to acrylic.',
      body: 'Cut from rigid foam PVC sheet, easy to fix and noticeably lighter than acrylic, which suits larger names or logos where weight matters. Paint or vinyl finished to match your brand colours.',
      image: '/home/signboards/foamex-panel.jpg',
      specs: [
        { label: 'Thickness', value: '10, 15 or 19mm sheet depending on letter size.' },
        { label: 'Finish', value: 'Painted or vinyl wrapped to your brand colours.' },
        { label: 'Fixings', value: 'Flush-fixed or stand-off locators for a shadow gap.' },
      ],
    },
    {
      id: 'built-up-acrylic',
      title: 'Built-up acrylic letters',
      lead: 'Fabricated letters with real depth, front, back and return.',
      body: 'Each letter is built up from separate acrylic faces and returns rather than cut from one flat sheet, giving genuine three-dimensional depth. The premium option for a fascia that needs to make a statement.',
      image: '/home/signboards/sign-tray.jpg',
      specs: [
        { label: 'Depth', value: 'Return depth built to suit your fascia and viewing distance.' },
        { label: 'Colours', value: 'Any colour face, with a matt or gloss finish.' },
        { label: 'Fixings', value: 'Stand-off locators, spaced behind each letter.' },
      ],
    },
    {
      id: 'illuminated',
      title: 'Illuminated letters',
      lead: 'The same built-up construction with LED lighting built in.',
      body: 'Front-lit, halo-lit or a combination of both, wired through a low-voltage transformer. A good option where a fascia needs to work after dark as well as during the day.',
      image: '/home/signboards/signboards-outpost.jpg',
      specs: [
        { label: 'Lighting', value: 'Front-lit, halo-lit (reverse) or both.' },
        { label: 'Power', value: 'Low-voltage LED, run from a transformer fitted on site.' },
        { label: 'Fixings', value: 'Stand-off locators, spaced to carry the cabling.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Vinyl-wrapped faces for lettering where a flat colour will not do, including gradients and photographic finishes.',
    },
    {
      title: 'Cut vinyl',
      text: 'Painted or coloured acrylic faces finished with a machine-cut vinyl detail, such as an outline or a second colour.',
    },
  ],

  gallery: [
    '/home/signboards/acrylic-panel.jpg',
    '/home/signboards/foamex-panel.jpg',
    '/home/signboards/sign-tray.jpg',
    '/home/signboards/signboards-outpost.jpg',
    '/home/signboards/signboards-mayfield.jpg',
    '/home/signboards/signboards-kearns-and-rouse.jpg',
    '/home/signboards/signboard-installation.jpg',
  ],

  info: [
    {
      title: 'Professional installation',
      text: 'Our insured install team fits lettering directly to render, brick, cladding or an existing fascia board.',
      bullets: ['Fully insured', 'Cabling for illuminated sets run and connected on site'],
    },
    {
      title: 'Supply only',
      text: 'Letters supplied with a fixing template and all the hardware you need to fit them yourself.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Turnaround',
      text: 'Production is usually 5 to 10 working days from approval. Need it sooner? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'What is the difference between flat-cut and built-up letters?',
      a: 'Flat-cut letters are cut from a single sheet, so they are flat with depth only in the material thickness. Built-up letters are fabricated from separate faces and returns, giving genuine three-dimensional depth.',
    },
    {
      q: 'Can lettering be illuminated?',
      a: 'Yes, built-up acrylic letters can be front-lit, halo-lit or both, wired to a transformer we fit as part of the installation.',
    },
    {
      q: 'Do you work from our existing logo?',
      a: 'Yes, we cut and build lettering to your exact logo file. If you only have a low-resolution version, our design team can redraw it before we cut anything.',
    },
  ],

  cta: {
    title: 'Get your free lettering quote and mockup.',
    text: 'Send us your logo and we will recommend the right material, depth and fixing for your fascia.',
    image: '/home/signboards/signboards-outpost.jpg',
  },
};

export default raisedLettering;
